/**
 * swup-init.js
 * Handles all Swup page transition logic:
 *  - Video teardown before transition (fixes Firefox abort error)
 *  - Video re-initialization after transition (event-driven, not setTimeout)
 *  - Audio persistence across pages
 *  - Background videos kept playing while browsing thumbnails
 *  - External link handling in nav drawer
 */

(function () {
  'use strict';

  // ─── SWUP INSTANCE ──────────────────────────────────────────────────────────
  const swup = new Swup({
    containers: ['#MainContent'],
    animateHistoryBrowsing: true,
  });

  window._swup = swup;

  // ─── pauseAllMedia SHIM + OVERRIDE ──────────────────────────────────────────
  /**
   * Two problems solved here in one block:
   *
   * PROBLEM 1 — Thumbnails broken on first Swup navigation:
   * media-gallery.js calls window.pauseAllMedia() on every thumbnail click.
   * global.js (defer) defines the real function. swup-init.js runs synchronously
   * at bottom of <body> BEFORE defer scripts execute. So on first page load,
   * pauseAllMedia is undefined when the first thumbnail click fires.
   * Fix: install a working shim immediately. global.js will overwrite it with
   * the real version when it runs — that's fine, both versions work.
   *
   * PROBLEM 2 — Background video pauses on thumbnail click:
   * The real pauseAllMedia (and our shim) both pause ALL videos. We want to
   * skip background videos (autoplay + muted + loop).
   * Fix: our shim already skips them. And we re-install a background-aware
   * version via DOMContentLoaded to overwrite global.js's version once it loads.
   */

  // Install shim immediately — works before global.js runs
  window.pauseAllMedia = function () {
    document.querySelectorAll('.js-youtube').forEach(function (v) {
      v.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
    });
    document.querySelectorAll('.js-vimeo').forEach(function (v) {
      v.contentWindow.postMessage('{"method":"pause"}', '*');
    });
    document.querySelectorAll('video').forEach(function (video) {
      var isBackground = video.hasAttribute('autoplay')
        && video.hasAttribute('muted')
        && video.hasAttribute('loop');
      if (!isBackground) video.pause();
    });
    document.querySelectorAll('product-model').forEach(function (model) {
      if (model.modelViewerUI) model.modelViewerUI.pause();
    });
  };

  // Re-install after DOMContentLoaded to overwrite global.js's plain version.
  // DOMContentLoaded fires after all defer scripts, so global.js has run by then.
  document.addEventListener('DOMContentLoaded', function () {
    window.pauseAllMedia = function () {
      document.querySelectorAll('.js-youtube').forEach(function (v) {
        v.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
      });
      document.querySelectorAll('.js-vimeo').forEach(function (v) {
        v.contentWindow.postMessage('{"method":"pause"}', '*');
      });
      document.querySelectorAll('video').forEach(function (video) {
        var isBackground = video.hasAttribute('autoplay')
          && video.hasAttribute('muted')
          && video.hasAttribute('loop');
        if (!isBackground) video.pause();
      });
      document.querySelectorAll('product-model').forEach(function (model) {
        if (model.modelViewerUI) model.modelViewerUI.pause();
      });
    };
  });

  // ─── AUDIO STATE ────────────────────────────────────────────────────────────
  var userHasPlayedAudio = false;
  var siteAudio = document.getElementById('site-audio');

  if (siteAudio) {
    siteAudio.addEventListener('play', function () {
      userHasPlayedAudio = true;
      siteAudio.removeAttribute('data-user-paused');
    });
    siteAudio.addEventListener('pause', function () {
      if (window.location.pathname !== '/pages/beats') {
        siteAudio.setAttribute('data-user-paused', 'true');
      }
    });
  }

  function handleAudioForPage() {
    var audioPlayer = document.getElementById('persistent-audio-player');
    var audio = document.getElementById('site-audio');
    if (!audioPlayer || !audio) return;

    if (window.location.pathname === '/pages/beats') {
      audioPlayer.style.display = 'none';
      audio.pause();
    } else {
      audioPlayer.style.display = 'block';
      if (userHasPlayedAudio && audio.paused && !audio.hasAttribute('data-user-paused')) {
        audio.play().catch(function () {});
      }
    }
  }

  // ─── VIDEO MANAGEMENT ───────────────────────────────────────────────────────

  function teardownVideos() {
    document.querySelectorAll('video').forEach(function (video) {
      try {
        video.pause();
        video.removeAttribute('src');
        video.querySelectorAll('source').forEach(function (s) { s.removeAttribute('src'); });
        video.load();
      } catch (e) {}
    });
  }

  function initVideo(video) {
    video.muted = true;
    video.setAttribute('playsinline', '');
    var source = video.querySelector('source');
    var src = video.getAttribute('src') || (source && source.getAttribute('src'));
    if (!src) return;
    if (!video.getAttribute('src') && source) source.setAttribute('src', src);
    var onCanPlay = function () {
      video.removeEventListener('canplay', onCanPlay);
      video.play().catch(function () {});
    };
    video.addEventListener('canplay', onCanPlay);
    video.load();
  }

  function initAllVideos() {
    document.querySelectorAll('video').forEach(initVideo);
  }

  // ─── FIRST INTERACTION (mobile autoplay unlock) ──────────────────────────
  var userHasInteracted = false;

  function handleFirstInteraction() {
    if (userHasInteracted) return;
    userHasInteracted = true;
    document.querySelectorAll('video').forEach(function (video) {
      video.muted = true;
      video.play().catch(function () {});
    });
    document.removeEventListener('touchstart', handleFirstInteraction);
    document.removeEventListener('click', handleFirstInteraction);
  }

  document.addEventListener('touchstart', handleFirstInteraction, { passive: true });
  document.addEventListener('click', handleFirstInteraction);

  // ─── EXTERNAL LINKS ──────────────────────────────────────────────────────
  function setupExternalLinks() {
    var drawer = document.querySelector('#Details-menu-drawer-container');
    if (!drawer) return;

    drawer.querySelectorAll('a[href^="http"]').forEach(function (link) {
      var href = link.getAttribute('href');
      if (!href || href.includes(window.location.hostname)) return;
      var fresh = link.cloneNode(true);
      link.parentNode.replaceChild(fresh, link);
      fresh.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        window.open(href, '_blank', 'noopener,noreferrer');
      });
    });
  }

  // ─── MENU CLOSE ON ANY LINK CLICK ────────────────────────────────────────
  function setupMenuCloseBehavior() {
    var drawer = document.querySelector('#Details-menu-drawer-container');
    if (!drawer) return;

    // Close menu when clicking any internal link (including current page)
    drawer.querySelectorAll('a').forEach(function (link) {
      link.addEventListener('click', function () {
        var detailsElement = document.querySelector('#Details-menu-drawer-container');
        if (detailsElement && detailsElement.hasAttribute('open')) {
          var summary = detailsElement.querySelector('summary');
          if (summary) summary.click();
        }
      });
    });
  }

  // ─── SWUP HOOKS ─────────────────────────────────────────────────────────────

  swup.hooks.on('visit:start', function () {
    // Close mobile menu drawer before navigation begins
    var detailsElement = document.querySelector('#Details-menu-drawer-container');
    if (detailsElement && detailsElement.hasAttribute('open')) {
      var summary = detailsElement.querySelector('summary');
      if (summary) summary.click();
    }
    
    // Remove any orphaned product modals from previous pages
    // These live outside #MainContent so Swup doesn't clean them up
    document.querySelectorAll('product-modal').forEach(function(modal) {
      modal.remove();
    });
    
    teardownVideos();
  });

  swup.hooks.on('content:replace', function () {
    // Swup injects HTML via innerHTML which skips custom element constructors.
    // customElements.upgrade() forces the browser to run constructors on all
    // registered custom elements found in the new content.
    var mainContent = document.getElementById('MainContent');
    if (mainContent) customElements.upgrade(mainContent);
  });

  swup.hooks.on('page:view', function () {
    handleAudioForPage();
    setupExternalLinks();
    setupMenuCloseBehavior();
    initAllVideos();
  });

  // ─── INITIAL PAGE LOAD ───────────────────────────────────────────────────
  handleAudioForPage();
  initAllVideos();
  setupExternalLinks();
  setupMenuCloseBehavior();

})();